// WP6-B.5 — minimal admin-only tiptap rich-text editor.
//
// Mounted from the three Marketing CMS pages (About + Projects dialog).
// The container carries `.rte-content` so `/app/frontend/src/styles/
// rte.css` can scope placeholder, heading, list, blockquote, hr, and
// link styles without touching the public G4 pages.

import React, { useEffect, useMemo } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import {
    Bold,
    Italic,
    List,
    ListOrdered,
    Quote,
    Minus,
    Link as LinkIcon,
    Undo2,
    Redo2,
} from "lucide-react";

function Toolbar({ editor }) {
    if (!editor) return null;
    const setLink = () => {
        const prev = editor.getAttributes("link").href || "";
        const url = window.prompt("URL", prev);
        if (url === null) return;
        if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
        }
        editor
            .chain()
            .focus()
            .extendMarkRange("link")
            .setLink({ href: url })
            .run();
    };
    const changeHeading = (e) => {
        const v = e.target.value;
        if (v === "p") {
            editor.chain().focus().setParagraph().run();
        } else {
            const level = Number(v.replace("h", ""));
            editor.chain().focus().toggleHeading({ level }).run();
        }
    };
    const activeHeading = editor.isActive("heading", { level: 2 })
        ? "h2"
        : editor.isActive("heading", { level: 3 })
        ? "h3"
        : "p";
    // Toolbar buttons receive a thunk, not a pre-built chain. Earlier
    // the chain was constructed inline at render time
    // (`editor.chain().focus().toggleBold()`), which — because
    // `createChain` in @tiptap/core v3 dispatches each chained command
    // immediately with `shouldDispatch=true` — fired a `focus` command
    // on every Toolbar render. Any sibling admin-modal input (Title,
    // Category, Location) then lost focus to the editor after each
    // keystroke, because the parent re-render re-ran the Toolbar's
    // chain expressions. Thunks defer the chain to click time, which
    // is the pattern used in all official Tiptap examples.
    const btn = (label, run, active, Icon) => (
        <button
            type="button"
            title={label}
            aria-label={label}
            data-testid={`rte-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={active ? "is-active" : ""}
            onClick={run}
        >
            <Icon size={14} />
        </button>
    );
    return (
        <div className="rte-toolbar" data-testid="rte-toolbar">
            <select
                aria-label="Heading"
                data-testid="rte-heading-select"
                value={activeHeading}
                onChange={changeHeading}
            >
                <option value="p">Paragraph</option>
                <option value="h2">Heading 2</option>
                <option value="h3">Heading 3</option>
            </select>
            {btn("Bold", () => editor.chain().focus().toggleBold().run(), editor.isActive("bold"), Bold)}
            {btn("Italic", () => editor.chain().focus().toggleItalic().run(), editor.isActive("italic"), Italic)}
            {btn("Bulleted list", () => editor.chain().focus().toggleBulletList().run(), editor.isActive("bulletList"), List)}
            {btn("Numbered list", () => editor.chain().focus().toggleOrderedList().run(), editor.isActive("orderedList"), ListOrdered)}
            {btn("Blockquote", () => editor.chain().focus().toggleBlockquote().run(), editor.isActive("blockquote"), Quote)}
            <button
                type="button"
                title="Horizontal rule"
                aria-label="Horizontal rule"
                data-testid="rte-hr"
                onClick={() => editor.chain().focus().setHorizontalRule().run()}
            >
                <Minus size={14} />
            </button>
            <button
                type="button"
                title="Link"
                aria-label="Link"
                data-testid="rte-link"
                className={editor.isActive("link") ? "is-active" : ""}
                onClick={setLink}
            >
                <LinkIcon size={14} />
            </button>
            <button
                type="button"
                title="Undo"
                aria-label="Undo"
                data-testid="rte-undo"
                onClick={() => editor.chain().focus().undo().run()}
            >
                <Undo2 size={14} />
            </button>
            <button
                type="button"
                title="Redo"
                aria-label="Redo"
                data-testid="rte-redo"
                onClick={() => editor.chain().focus().redo().run()}
            >
                <Redo2 size={14} />
            </button>
        </div>
    );
}

export default function RichTextEditor({ content, onChange, placeholder }) {
    // Stabilise the extensions array across renders. @tiptap/react v3's
    // `useEditor` compares options by identity on every render; a drifting
    // extensions array would cause `editor.setOptions(...)` to re-run,
    // which rebuilds ProseMirror internals. Harmless in isolation, but
    // keeping it stable is cheap defensive hygiene.
    const extensions = useMemo(() => [
        StarterKit.configure({ heading: { levels: [2, 3] } }),
        Link.configure({ openOnClick: false, autolink: true, linkOnPaste: true }),
        Placeholder.configure({ placeholder: placeholder || "Start writing…" }),
    ], [placeholder]);

    const editor = useEditor({
        extensions,
        content: content || "",
        onUpdate: ({ editor: ed }) => {
            if (typeof onChange === "function") onChange(ed.getHTML());
        },
    });

    // Keep editor content in sync when parent form state is loaded
    // async (e.g. first `adminGetAbout()` promise resolve).
    useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        if ((content || "") !== current) {
            editor.commands.setContent(content || "", { emitUpdate: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content, editor]);

    return (
        <div className="rte-shell rte-content" data-testid="rich-text-editor">
            <Toolbar editor={editor} />
            <EditorContent editor={editor} />
        </div>
    );
}
